import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import config from "../../config";
import PopupMessage from "../layouts/PopupMessage";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaCalendarAlt,
  FaInfoCircle,
} from "react-icons/fa";
import "../css/css_pages/CreateClient.css";

const CreateCustomer = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    type: "",
    tso_id: "",
    customer_id: "",
    customer_name: "",
    address: "",
    ds_division: "",
    province: "",
    district: "",
    phone: "",
    manager: "",
    chairman: "",
    mobile: "",
    email: "",
    start_date: "",
    agreement_date: "",
    reg_number: "",
    reg_date: "",
    user_id: "",
    ds_id: "",
    latitude: "",
    longitude: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [dsList, setDsList] = useState([]);
  const [tsoList, setTsoList] = useState([]);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);

  // Sri Lanka provinces and districts
  const sriLankaProvinces = [
    {
      name: "Central Province",
      districts: ["Kandy", "Matale", "Nuwara Eliya"],
    },
    {
      name: "Eastern Province",
      districts: ["Ampara", "Batticaloa", "Trincomalee"],
    },
    {
      name: "Northern Province",
      districts: ["Jaffna", "Kilinochchi", "Mannar", "Mullaitivu", "Vavuniya"],
    },
    {
      name: "North Central Province",
      districts: ["Anuradhapura", "Polonnaruwa"],
    },
    { name: "North Western Province", districts: ["Kurunegala", "Puttalam"] },
    { name: "Sabragamuwa Province", districts: ["Kegalle", "Ratnapura"] },
    { name: "Southern Province", districts: ["Galle", "Hambantota", "Matara"] },
    { name: "Uva Province", districts: ["Badulla", "Monaragala"] },
    { name: "Western Province", districts: ["Colombo", "Gampaha", "Kalutara"] },
  ];

  // District to coordinates mapping
  const districtCoordinates = {
    Kandy: { latitude: "7.2906", longitude: "80.6337" },
    Matale: { latitude: "7.4653", longitude: "80.6222" },
    "Nuwara Eliya": { latitude: "6.9699", longitude: "80.7878" },
    Ampara: { latitude: "7.2906", longitude: "81.6697" },
    Batticaloa: { latitude: "7.7167", longitude: "81.6997" },
    Trincomalee: { latitude: "8.5833", longitude: "81.2333" },
    Jaffna: { latitude: "9.6615", longitude: "80.0255" },
    Kilinochchi: { latitude: "9.3856", longitude: "80.4000" },
    Mannar: { latitude: "8.9833", longitude: "79.9167" },
    Mullaitivu: { latitude: "9.2667", longitude: "80.8167" },
    Vavuniya: { latitude: "8.7500", longitude: "80.5000" },
    Anuradhapura: { latitude: "8.3333", longitude: "80.4167" },
    Polonnaruwa: { latitude: "7.9333", longitude: "81.0000" },
    Kurunegala: { latitude: "7.4833", longitude: "80.3500" },
    Puttalam: { latitude: "8.0333", longitude: "79.8333" },
    Kegalle: { latitude: "7.2500", longitude: "80.3333" },
    Ratnapura: { latitude: "6.6833", longitude: "80.4000" },
    Galle: { latitude: "6.0333", longitude: "80.2167" },
    Hambantota: { latitude: "6.1167", longitude: "81.1167" },
    Matara: { latitude: "5.9500", longitude: "80.5333" },
    Badulla: { latitude: "6.9667", longitude: "81.0500" },
    Monaragala: { latitude: "6.8667", longitude: "81.3500" },
    Colombo: { latitude: "6.9271", longitude: "79.8612" },
    Gampaha: { latitude: "7.0833", longitude: "79.9833" },
    Kalutara: { latitude: "6.5833", longitude: "80.2000" },
  };

  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    const userId = Cookies.get("userId");
    const authToken = Cookies.get("authToken");

    if (!userId || !authToken) {
      setAlertMessage({
        text: "Authentication required. Please login again.",
        isError: true,
      });
      navigate("/login");
      return;
    }

    setFormData((prev) => ({ ...prev, user_id: userId }));

    // Fetch DS list
    const fetchDsList = async () => {
      try {
        const response = await fetch(
          `${config.API_URL}/management/get-ds-list`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch DS list");
        }

        const responseData = await response.json();
        setDsList(responseData);
      } catch (error) {
        setAlertMessage({
          text: error.message || "An error occurred while fetching DS list",
          isError: true,
        });
      }
    };

    // Fetch TSO list
    const fetchTsoList = async () => {
      try {
        const response = await fetch(`${config.API_URL}/management/tso-list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch TSO list");
        }

        const responseData = await response.json();
        setTsoList(responseData);
        console.log("TSO LIST", responseData);
      } catch (error) {
        setAlertMessage({
          text: error.message || "An error occurred while fetching TSO list",
          isError: true,
        });
      }
    };

    fetchDsList();
    fetchTsoList();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "ds_division") {
      const selectedDs = dsList.find((ds) => ds.ds_name === value);
      if (selectedDs) {
        setFormData({
          ...formData,
          ds_division: value,
          ds_id: selectedDs.ds_id,
        });
      }
    } else if (name === "province") {
      const selectedProvince = sriLankaProvinces.find(
        (province) => province.name === value
      );
      if (selectedProvince) {
        setDistricts(selectedProvince.districts);
        setFormData({
          ...formData,
          province: value,
          district: "",
          latitude: "",
          longitude: "",
        });
      }
    } else if (name === "district") {
      const coordinates = districtCoordinates[value];
      setFormData({
        ...formData,
        district: value,
        latitude: coordinates ? coordinates.latitude : "",
        longitude: coordinates ? coordinates.longitude : "",
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const showMessage = (message) => {
    setAlertMessage({
      text: message,
      isError: true,
    });
  };

  const checkPhoneNumberExists = async (phone) => {
    try {
      setIsCheckingPhone(true);
      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/management/check-phone/${phone}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to check phone number");
      }

      const responseData = await response.json();

      if (responseData.exists) {
        showMessage("Phone number already exists");
      }

      return responseData.exists;
    } catch (error) {
      console.error("Error checking phone number:", error);
      return false;
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const handlePhoneBlur = async (e) => {
    const { name, value } = e.target;

    if (!value) {
      setErrors({
        ...errors,
        [name]: "Phone number is required",
      });
      return;
    }

    try {
      const exists = await checkPhoneNumberExists(value);
      if (exists) {
        setErrors({
          ...errors,
          [name]: "Phone number already exists",
        });
      } else {
        setErrors({
          ...errors,
          [name]: null,
        });
      }
    } catch (error) {
      setErrors({
        ...errors,
        [name]: "Error checking phone number",
      });
    }
  };

  const validateForm = async () => {
    const newErrors = {};
    const requiredFields = [
      "type",
      "customer_id",
      "customer_name",
      "address",
      "ds_division",
      "province",
      "district",
      "phone",
      "manager",
      "mobile",
      "start_date",
      "agreement_date",
    ];

    // Check required fields
    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${field.replace("_", " ")} is required`;
      }
    });

    // Customer ID validation
    if (!formData.customer_id) {
      newErrors.customer_id = "Customer ID is required";
    } else if (!/^[A-Za-z0-9]+$/.test(formData.customer_id)) {
      newErrors.customer_id = "Customer ID should be alphanumeric";
    }

    // Phone validation
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else {
      try {
        const exists = await checkPhoneNumberExists(formData.phone);
        if (exists) {
          newErrors.phone = "Phone number already exists";
        }
      } catch (error) {
        newErrors.phone = "Error validating phone number";
      }
    }

    // Mobile validation
    if (!formData.mobile) {
      newErrors.mobile = "Mobile number is required";
    }

    // Email validation
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = Cookies.get("authToken");
      const userId = Cookies.get("userId");

      if (!token || !userId) {
        setAlertMessage({
          text: "Authentication required. Please login again.",
          isError: true,
        });
        navigate("/login");
        return;
      }

      // Prepare the payload
      const payload = {
        ...formData,
        user_id: userId,
      };

      const response = await fetch(
        `${config.API_URL}/management/create-customer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.message ||
            "Failed to create customer or Phone number already exists"
        );
      }

      setAlertMessage({
        text: responseData.message || "Customer created successfully!",
        isError: false,
        onClose: () => navigate("/client-manage"),
      });

      // Reset form
      setFormData({
        type: "",
        tso_id: "",
        customer_id: "",
        customer_name: "",
        address: "",
        ds_division: "",
        province: "",
        district: "",
        phone: "",
        manager: "",
        chairman: "",
        mobile: "",
        email: "",
        start_date: "",
        agreement_date: "",
        reg_number: "",
        reg_date: "",
        user_id: userId,
        ds_id: "",
        latitude: "",
        longitude: "",
      });
    } catch (error) {
      setAlertMessage({
        text: error.message || "An error occurred while creating customer",
        isError: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePopup = () => {
    setAlertMessage(null);
    if (alertMessage && !alertMessage.isError) {
      navigate("/client-manage");
    }
  };

  const customerTypes = [
    { value: "SANASA Union", label: "SANASA Union" },
    { value: "Apex Federation", label: "Apex Federation" },
    { value: "COOP City", label: "COOP City" },
    { value: "COOP Society", label: "COOP Society" },
    { value: "CRB", label: "CRB" },
    { value: "CRB Union", label: "CRB Union" },
    { value: "Company", label: "Company" },
    { value: "NGO", label: "NGO" },
    { value: "PRDCS", label: "PRDCS" },
    { value: "SANASA Society", label: "SANASA Society" },
    { value: "Womens COOP", label: "Womens COOP" },
  ];

  return (
    <div className="customer-form-container">
      <div className="customer-header">
        <h2>Create New Customer</h2>
      </div>

      {alertMessage && (
        <PopupMessage
          message={alertMessage.text}
          isError={alertMessage.isError}
          onClose={handleClosePopup}
        />
      )}

      <form className="customer-form" onSubmit={handleSubmit}>
        {/* Customer Information Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>
              <FaInfoCircle /> Basic Information
            </h3>
          </div>

          <div className="form-group">
            <label>
              Customer ID <span className="required">*</span>
            </label>
            <input
              type="text"
              name="customer_id"
              value={formData.customer_id}
              onChange={handleChange}
              className={`form-control ${errors.customer_id ? "error" : ""}`}
              required
            />
            {errors.customer_id && (
              <span className="error-message">{errors.customer_id}</span>
            )}
          </div>

          <div className="form-group">
            <label>
              Customer Type <span className="required">*</span>
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className={`form-control ${errors.type ? "error" : ""}`}
              required
            >
              <option value="">Select Customer Type</option>
              {customerTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.type && (
              <span className="error-message">{errors.type}</span>
            )}
          </div>

          <div className="form-group">
            <label>
              Customer Name <span className="required">*</span>
            </label>
            <input
              type="text"
              name="customer_name"
              value={formData.customer_name}
              onChange={handleChange}
              className={`form-control ${errors.customer_name ? "error" : ""}`}
              required
            />
            {errors.customer_name && (
              <span className="error-message">{errors.customer_name}</span>
            )}
          </div>

          <div className="form-group">
            <label>
              Address <span className="required">*</span>
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              className={`form-control ${errors.address ? "error" : ""}`}
              required
            />
            {errors.address && (
              <span className="error-message">{errors.address}</span>
            )}
          </div>
        </div>

        {/* Location Information Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>
              <FaMapMarkerAlt /> Location Details
            </h3>
          </div>

          <div className="form-group">
            <label>
              DS Division <span className="required">*</span>
            </label>
            <select
              name="ds_division"
              value={formData.ds_division}
              onChange={handleChange}
              className={`form-control ${errors.ds_division ? "error" : ""}`}
              required
            >
              <option value="">Select DS Division</option>
              {dsList.map((ds) => (
                <option key={ds.ds_id} value={ds.ds_name}>
                  {ds.ds_name}
                </option>
              ))}
            </select>
            {errors.ds_division && (
              <span className="error-message">{errors.ds_division}</span>
            )}
          </div>

          <div className="form-group">
            <label>
              Province <span className="required">*</span>
            </label>
            <select
              name="province"
              value={formData.province}
              onChange={handleChange}
              className={`form-control ${errors.province ? "error" : ""}`}
              required
            >
              <option value="">Select Province</option>
              {sriLankaProvinces.map((province) => (
                <option key={province.name} value={province.name}>
                  {province.name}
                </option>
              ))}
            </select>
            {errors.province && (
              <span className="error-message">{errors.province}</span>
            )}
          </div>

          <div className="form-group">
            <label>
              District <span className="required">*</span>
            </label>
            <select
              name="district"
              value={formData.district}
              onChange={handleChange}
              className={`form-control ${errors.district ? "error" : ""}`}
              disabled={!formData.province}
              required
            >
              <option value="">Select District</option>
              {districts.map((district) => (
                <option key={district} value={district}>
                  {district}
                </option>
              ))}
            </select>
            {errors.district && (
              <span className="error-message">{errors.district}</span>
            )}
          </div>

          <div className="form-group">
            <label>TSO ID</label>
            <select
              name="tso_id"
              value={formData.tso_id}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">Select TSO</option>
              {tsoList.map((tso) => (
                <option key={tso.tso_tso_emp_no} value={tso.tso_emp_no}>
                  {tso.tso_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>DS ID</label>
            <input
              type="text"
              name="ds_id"
              value={formData.ds_id}
              className="form-control"
              readOnly
            />
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>
              <FaPhone /> Contact Information
            </h3>
          </div>

          <div className="form-group">
            <label>
              Phone <span className="required">*</span>
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handlePhoneBlur}
              className={`form-control ${errors.phone ? "error" : ""}`}
              required
            />
            {errors.phone && (
              <span className="error-message">
                {errors.phone}
                {isCheckingPhone && " (Checking...)"}
              </span>
            )}
          </div>

          <div className="form-group">
            <label>
              Manager <span className="required">*</span>
            </label>
            <input
              type="text"
              name="manager"
              value={formData.manager}
              onChange={handleChange}
              className={`form-control ${errors.manager ? "error" : ""}`}
              required
            />
            {errors.manager && (
              <span className="error-message">{errors.manager}</span>
            )}
          </div>

          <div className="form-group">
            <label>Chairman</label>
            <input
              type="text"
              name="chairman"
              value={formData.chairman}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>
              Contact Person's Phone Number <span className="required">*</span>
            </label>
            <lable className="form-label-text">
              (The company should specify who to contact for client issues.)
            </lable>
            <input
              type="text"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              className={`form-control ${errors.mobile ? "error" : ""}`}
              required
            />
            {errors.mobile && (
              <span className="error-message">{errors.mobile}</span>
            )}
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`form-control ${errors.email ? "error" : ""}`}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>
        </div>

        {/* Dates & Registration Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>
              <FaCalendarAlt /> Dates & Registration
            </h3>
          </div>

          <div className="form-group">
            <label>
              Start Date <span className="required">*</span>
            </label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className={`form-control ${errors.start_date ? "error" : ""}`}
              required
            />
            {errors.start_date && (
              <span className="error-message">{errors.start_date}</span>
            )}
          </div>

          <div className="form-group">
            <label>
              Agreement Date <span className="required">*</span>
            </label>
            <input
              type="date"
              name="agreement_date"
              value={formData.agreement_date}
              onChange={handleChange}
              className={`form-control ${errors.agreement_date ? "error" : ""}`}
              required
            />
            {errors.agreement_date && (
              <span className="error-message">{errors.agreement_date}</span>
            )}
          </div>

          <div className="form-group">
            <label>Registration Number</label>
            <input
              type="text"
              name="reg_number"
              value={formData.reg_number}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label>Registration Date</label>
            <input
              type="date"
              name="reg_date"
              value={formData.reg_date}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        {/* Coordinates Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>
              <FaMapMarkerAlt /> Coordinates
            </h3>
          </div>

          <div className="form-group">
            <label>Latitude</label>
            <input
              type="text"
              name="latitude"
              value={formData.latitude}
              className="form-control"
              readOnly
            />
          </div>

          <div className="form-group">
            <label>Longitude</label>
            <input
              type="text"
              name="longitude"
              value={formData.longitude}
              className="form-control"
              readOnly
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-submit"
            disabled={isSubmitting || isCheckingPhone}
          >
            {isSubmitting ? "Creating..." : "Create Customer"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCustomer;